import clsx from 'clsx';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import colors from '../../themes/main-colors';
import { FormState } from '../../hooks/useFormState.types';
import isNil from 'lodash/isNil';
import usePrevious from '../../hooks/usePrevious';
import { useEffect, useRef } from 'react';

const useStyles = makeStyles(() =>
  createStyles({
    inputNotActive: {
      '& .MuiInputBase-input': {
        color: `${colors.lightGray1}`,
        '&:-internal-autofill-selected': {
          color: `${colors.lightGray1} !important`,
          '-webkit-text-fill-color': `${colors.lightGray1} !important`,
          backgroundColor: 'white !important',
          '-webkit-box-shadow': '0 0 0 30px white inset !important',
        },
        '&:focus': {
          color: 'currentColor',
          '&:-internal-autofill-selected': {
            color: 'currentColor !important',
            '-webkit-text-fill-color': 'currentColor !important',
            backgroundColor: 'white !important',
            '-webkit-box-shadow': '0 0 0 30px white inset !important',
          },
        },
      },
    },
    input: {
      width: '100%',
    },
  }),
);

export function useMakeSelectProps<Model extends Record<string, any>>(
  form: FormState<Model>,
  selectorField: keyof Model,
  fields: Array<keyof Model>,
) {
  const classes = useStyles();
  const selectorValue = form.values[selectorField];
  const previousValues = usePrevious(form.values);
  const refs = fields.map(() => useRef(null));

  useEffect(() => {
    // If the field changes values, but the selector is not highlighting the field,
    // change the selector to the field
    if (previousValues !== null) {
      fields.forEach(async field => {
        if (
          selectorValue !== field &&
          previousValues[field] !== form.values[field]
        ) {
          await form.setValue(selectorField, field);
        }
      });
    }
  }, [previousValues, form.values]);

  return {
    selectorValue,
    makeProps: (fieldName: keyof Model, props: any) => ({
      className: clsx(
        classes.input,
        selectorValue !== fieldName && classes.inputNotActive,
        props.className,
      ),
      margin: 'normal',
      onMouseDown: (e: React.MouseEvent) => {
        const target = e.target;
        const listener = async () => {
          await form.setValue(selectorField, fieldName);
          target.removeEventListener('focus', listener);
        };
        target.addEventListener('focus', listener);
      },
      ...form.props(fieldName),
      error: !isNil(form.errors[fieldName]) && selectorValue === fieldName,
      helperText: undefined,
    }),
    refs,
  };
}
